import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  ButtonGroup,
  Chip,
  Stack,
  TextField,
  InputAdornment,
  IconButton,
  Icon,
  useTheme,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import FormularioReserva from './FormularioReservas';

const Tablero = ({ rifa, onReservaSuccess }) => {
  const theme = useTheme();
  const [numerosSeleccionados, setNumerosSeleccionados] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [formularioOpen, setFormularioOpen] = useState(false);
  const [modalInactivoOpen, setModalInactivoOpen] = useState(false);
  const [modalGanadorOpen, setModalGanadorOpen] = useState(false);
  const [boletoGanadorSeleccionado, setBoletoGanadorSeleccionado] = useState(null);

  const isActiva = String(rifa?.activo) === '1';
  const numeros = useMemo(() => (Array.isArray(rifa?.numeros) ? rifa.numeros : []), [rifa?.numeros]);

  // Números disponibles para selección
  const numerosDisponibles = useMemo(() => {
    return numeros.filter((item) => String(item.estado) === '1');
  }, [numeros]);

  // Manejo de selección individual
  const handleSeleccionarNumero = (numero) => {
    if (!isActiva) {
      setModalInactivoOpen(true);
      return;
    }
    setNumerosSeleccionados((prev) => {
      if (prev.includes(numero)) {
        return prev.filter((num) => num !== numero);
      } else {
        return [...prev, numero];
      }
    });
  };

  // Selección al azar inteligente
  const handleSeleccionarAzar = (cantidad) => {
    if (!isActiva) {
      setModalInactivoOpen(true);
      return;
    }
    const disponiblesNoSeleccionados = numerosDisponibles.filter(
      (item) => !numerosSeleccionados.includes(item.numero)
    );

    if (disponiblesNoSeleccionados.length === 0) return;

    // Shuffle rápido
    const shuffled = [...disponiblesNoSeleccionados].sort(() => 0.5 - Math.random());
    const seleccionadosNuevos = shuffled.slice(0, cantidad).map((item) => item.numero);

    setNumerosSeleccionados((prev) => [...prev, ...seleccionadosNuevos]);
  };

  const handleOpenFormulario = () => {
    if (numerosSeleccionados.length > 0 && isActiva) {
      setFormularioOpen(true);
    }
  };

  const handleCloseFormulario = () => {
    setFormularioOpen(false);
  };

  const handleFormularioSuccess = () => {
    setNumerosSeleccionados([]);
    setFormularioOpen(false);
    if (onReservaSuccess) {
      onReservaSuccess();
    }
  };

  const handleSolicitarReserva = () => {
    if (!isActiva) {
      setModalInactivoOpen(true);
      return;
    }

    // 1. Obtener código de país y teléfono (prioriza vendedor si la url es de vendedor, luego dueño)
    const codigoPaisRaw = String(rifa.codigo_pais || rifa.codigoPais || '57').replace(/\D/g, '');
    const telefonoRaw = String(rifa.telefono_movil || rifa.telefono || rifa.telefono_notificaciones || '').replace(/\D/g, '');

    if (!telefonoRaw) {
      alert('Esta rifa no tiene configurado un número de contacto para WhatsApp.');
      return;
    }

    // Asegurarse de que incluya el prefijo del país
    const fullPhone = telefonoRaw.startsWith(codigoPaisRaw) ? telefonoRaw : `${codigoPaisRaw}${telefonoRaw}`;
    const texto = `Hola, quiero reservar los siguientes números para la rifa "${rifa.titulo}": ${numerosSeleccionados.join(', ')}`;
    const url = `https://api.whatsapp.com/send?phone=${fullPhone}&text=${encodeURIComponent(texto)}`;
    window.open(url, '_blank');
  };

  const handleRemoveNumber = (numeroToRemove) => {
    setNumerosSeleccionados((prev) => prev.filter((num) => num !== numeroToRemove));
  };

  const handleDeseleccionarTodos = () => {
    setNumerosSeleccionados([]);
  };

  const totalPagar = (numerosSeleccionados.length * parseFloat(rifa?.precio || 0)).toLocaleString('es-CO');

  const filteredNumeros = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return numeros;
    return numeros.filter((item) => String(item.numero).toLowerCase().includes(query));
  }, [numeros, searchQuery]);

  const [limiteRenderizado, setLimiteRenderizado] = useState(100);

  useEffect(() => {
    setLimiteRenderizado(100);
  }, [searchQuery]);

  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollTop + clientHeight >= scrollHeight - 80) {
      setLimiteRenderizado((prev) => Math.min(prev + 100, filteredNumeros.length));
    }
  };

  const getFontSize = (numero) => {
    const len = String(numero).length;
    if (len <= 2) return { xs: '0.8rem', sm: '0.86rem' };
    if (len === 3) return { xs: '0.7rem', sm: '0.74rem' };
    return { xs: '0.62rem', sm: '0.66rem' };
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Banner destacado si la rifa está inactiva */}
      {!isActiva && (
        <Box
          sx={{
            mb: 2,
            p: 1.6,
            borderRadius: '12px',
            bgcolor: 'rgba(239, 68, 68, 0.08)',
            border: '1.5px solid rgba(239, 68, 68, 0.3)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '8px',
              bgcolor: 'rgba(239, 68, 68, 0.15)',
              color: '#ef4444',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 24 }}>lock_clock</Icon>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ color: '#991b1b', fontWeight: 900, fontSize: { xs: '0.95rem', sm: '1.05rem' }, lineHeight: 1.25 }}>
              Rifa Inactiva o Finalizada
            </Typography>
            <Typography variant="caption" sx={{ color: '#b91c1c', fontWeight: 600, mt: 0.2, fontSize: '0.78rem', display: 'block' }}>
              Esta rifa ya no se encuentra activa para reservas o compras de boletos.
            </Typography>
          </Box>
        </Box>
      )}

      {/* Barra de herramientas: Buscador + Selección Rápida en 1 Sola Línea */}
      <Box
        sx={{
          display: 'flex',
          gap: 1.2,
          mb: 1.5,
          alignItems: 'center',
          flexWrap: { xs: 'wrap', sm: 'nowrap' },
        }}
      >
        <TextField
          size="small"
          placeholder="Buscar tu número..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          sx={{
            flex: { xs: '1 1 100%', sm: 1 },
            minWidth: { xs: '100%', sm: 200 },
            '& .MuiOutlinedInput-root': {
              borderRadius: '8px',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : '#ffffff',
              height: '42px',
              '& fieldset': { borderColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : '#e2e8f0' },
              '&:hover fieldset': { borderColor: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main },
            },
            '& .MuiOutlinedInput-input': {
              py: 1,
              fontSize: '0.9rem',
              color: theme.palette.text.primary,
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Icon sx={{ color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontSize: 20 }}>search</Icon>
              </InputAdornment>
            ),
            endAdornment: searchQuery ? (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setSearchQuery('')}>
                  <Icon sx={{ fontSize: 18, color: theme.palette.text.secondary }}>close</Icon>
                </IconButton>
              </InputAdornment>
            ) : null,
          }}
        />

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: '42px',
            gap: 0.8,
            px: 1,
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.04)' : 'rgba(45, 52, 132, 0.04)',
            borderRadius: '8px',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : '1px solid #e2e8f0',
            opacity: isActiva ? 1 : 0.6,
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 800, color: theme.palette.text.secondary, display: { xs: 'none', sm: 'inline-flex' }, alignItems: 'center', gap: 0.4, fontSize: '0.8rem' }}>
            🎲 Azar:
          </Typography>
          <ButtonGroup disabled={!isActiva} size="small" variant="outlined" color="primary" sx={{ borderRadius: '6px', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.06)' : '#ffffff', height: '32px' }}>
            <Button onClick={() => handleSeleccionarAzar(1)} sx={{ fontWeight: 800, px: 1.2, fontSize: '0.8rem', borderRadius: '6px 0 0 6px' }}>+1</Button>
            <Button onClick={() => handleSeleccionarAzar(3)} sx={{ fontWeight: 800, px: 1.2, fontSize: '0.8rem' }}>+3</Button>
            <Button onClick={() => handleSeleccionarAzar(5)} sx={{ fontWeight: 800, px: 1.2, fontSize: '0.8rem' }}>+5</Button>
            <Button onClick={() => handleSeleccionarAzar(10)} sx={{ fontWeight: 800, px: 1.2, fontSize: '0.8rem', borderRadius: '0 6px 6px 0' }}>+10</Button>
          </ButtonGroup>
        </Box>
      </Box>

      {/* Guía interactiva cuando no hay boletos seleccionados */}
      {numerosSeleccionados.length === 0 && (
        <Box
          sx={{
            mb: 1.4,
            p: 1.2,
            borderRadius: '10px',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.08)' : 'rgba(45, 52, 132, 0.04)',
            border: theme.palette.mode === 'dark' ? '1.5px dashed rgba(99, 102, 241, 0.25)' : '1.5px dashed rgba(45, 52, 132, 0.18)',
            display: 'flex',
            alignItems: 'center',
            gap: 1.4,
          }}
        >
          <Box
            sx={{
              width: 38,
              height: 38,
              borderRadius: '8px',
              bgcolor: theme.palette.mode === 'dark' ? 'rgba(99, 102, 241, 0.16)' : 'rgba(45, 52, 132, 0.08)',
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            <Icon sx={{ fontSize: 22, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main }}>touch_app</Icon>
          </Box>
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 900, color: theme.palette.text.primary, fontSize: '0.88rem', lineHeight: 1.2 }}>
              Selecciona los boletos
            </Typography>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontSize: '0.78rem', display: 'block', mt: 0.2 }}>
              Toca cualquier número disponible para seleccionarlo
            </Typography>
          </Box>
        </Box>
      )}

      {/* Resumen flotante de boletos seleccionados */}
      {numerosSeleccionados.length > 0 && (
        <Box
          sx={{
            mb: 1.6,
            p: 1.4,
            borderRadius: '10px',
            bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.03)' : '#f8fafc',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
            boxShadow: theme.palette.mode === 'dark' ? '0 4px 12px rgba(0, 0, 0, 0.3)' : '0 2px 6px rgba(0, 0, 0, 0.02)',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', fontWeight: 900, color: theme.palette.text.primary, fontSize: '0.85rem' }}>
              <Icon sx={{ mr: 0.8, color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontSize: 18 }}>confirmation_number</Icon>
              Boletos Seleccionados ({numerosSeleccionados.length})
            </Typography>
            <Button
              variant="text"
              color="error"
              size="small"
              onClick={handleDeseleccionarTodos}
              sx={{ fontWeight: 800, fontSize: '0.72rem', py: 0.1, px: 0.5 }}
            >
              Deseleccionar
            </Button>
          </Box>

          <Stack direction="row" spacing={0.6} flexWrap="wrap" useFlexGap sx={{ mb: 1.2 }}>
            {numerosSeleccionados.map((numero) => (
              <Chip
                key={numero}
                label={numero}
                size="small"
                onDelete={() => handleRemoveNumber(numero)}
                sx={{
                  bgcolor: '#e11d48',
                  color: 'white',
                  fontWeight: 900,
                  borderRadius: '6px',
                  fontSize: '0.8rem',
                  height: 24,
                  boxShadow: '0 2px 4px rgba(225, 29, 72, 0.25)',
                  '& .MuiChip-deleteIcon': {
                    color: 'rgba(255, 255, 255, 0.85)',
                    fontSize: 15,
                    '&:hover': { color: 'white' },
                  },
                }}
              />
            ))}
          </Stack>

          {/* Subtotal en vivo */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pt: 0.8, borderTop: theme.palette.mode === 'dark' ? '1px dashed rgba(255, 255, 255, 0.15)' : '1px dashed rgba(45, 52, 132, 0.2)' }}>
            <Typography variant="caption" sx={{ color: theme.palette.text.secondary, fontWeight: 800, fontSize: '0.8rem' }}>
              Total estimado a pagar:
            </Typography>
            <Typography variant="subtitle1" sx={{ color: theme.palette.mode === 'dark' ? '#818cf8' : theme.palette.primary.main, fontWeight: 900, fontSize: '1.15rem' }}>
              $ {totalPagar}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Cuadrícula de números con scroll propio y lazy loading progresivo */}
      <Box
        onScroll={handleScroll}
        sx={{
          maxHeight: { xs: '320px', sm: '350px', md: '400px' },
          overflowY: 'auto',
          p: 1.2,
          bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.02)' : '#f8fafc',
          borderRadius: '10px',
          border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid #e2e8f0',
        }}
      >
        <Grid container spacing={0.5} justifyContent="center">
          {filteredNumeros.slice(0, limiteRenderizado).map((item) => {
            const isGanador = item.ganador === '1' || item.ganador === true || item.ganador === 1;
            const isSelected = numerosSeleccionados.includes(item.numero);
            let colorTexto = '#ffffff';
            let backgroundColor = 'transparent';
            let emoji = '';

            if (isGanador) {
              colorTexto = '#ffffff';
              backgroundColor = '#f59e0b';
              emoji = '🏆';
            } else {
              switch (item.estado) {
                case '1':
                  colorTexto = isSelected ? '#ffffff' : (rifa.color_texto_disponibles || '#ffffff');
                  backgroundColor = isSelected ? '#e11d48' : (rifa.color_fondo_disponibles || '#2d3484');
                  break;
                case '2':
                  colorTexto = rifa.color_texto_reservadas || '#ffffff';
                  backgroundColor = rifa.color_fondo_reservadas || '#f59e0b';
                  emoji = rifa.emoji_reservadas || '🔒';
                  break;
                case '3':
                  colorTexto = rifa.color_texto_pagadas || '#ffffff';
                  backgroundColor = rifa.color_fondo_pagadas || '#10b981';
                  emoji = rifa.emoji_pagadas || '⭐';
                  break;
                default:
                  break;
              }
            }

            return (
              <Grid
                key={`${item.id}-${item.numero}`}
                item
                xs={2.4}
                sm={1.2}
                sx={{ display: 'flex', justifyContent: 'center' }}
              >
                <Box
                  onClick={() => {
                    if (isGanador) {
                      setBoletoGanadorSeleccionado(item.numero);
                      setModalGanadorOpen(true);
                      return;
                    }
                    if (!isActiva) {
                      setModalInactivoOpen(true);
                      return;
                    }
                    if (item.estado === '1') {
                      handleSeleccionarNumero(item.numero);
                    }
                  }}
                  sx={{
                    width: '100%',
                    aspectRatio: '1 / 1',
                    minWidth: { xs: 30, sm: 34 },
                    minHeight: { xs: 30, sm: 34 },
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    bgcolor: backgroundColor,
                    color: colorTexto,
                    cursor: isGanador ? 'pointer' : (isActiva ? (item.estado === '1' ? 'pointer' : 'not-allowed') : 'pointer'),
                    opacity: (!isActiva && item.estado === '1') ? 0.6 : 1,
                    boxShadow: isGanador
                      ? '0 0 10px rgba(245, 158, 11, 0.6)'
                      : (isSelected ? '0 2px 8px rgba(225, 29, 72, 0.4)' : '0 1px 2px rgba(0,0,0,0.03)'),
                    transform: isSelected || isGanador ? 'scale(1.06)' : 'scale(1)',
                    transition: 'all 0.12s ease',
                    userSelect: 'none',
                    border: isGanador
                      ? '2px solid #fef08a'
                      : (isSelected ? '1.5px solid #ffffff' : '1px solid rgba(255,255,255,0.12)'),
                    '&:hover': {
                      transform: isActiva && item.estado === '1' ? 'scale(1.08)' : (isGanador ? 'scale(1.1)' : 'none'),
                      boxShadow: isGanador
                        ? '0 0 14px rgba(245, 158, 11, 0.8)'
                        : (isActiva && item.estado === '1' ? '0 2px 8px rgba(45, 52, 132, 0.2)' : 'none'),
                      zIndex: 2,
                    },
                  }}
                >
                  {isGanador ? (
                    <Typography sx={{ fontSize: { xs: 13, sm: 15 }, textAlign: 'center' }}>
                      🏆
                    </Typography>
                  ) : item.estado === '1' ? (
                    <Typography sx={{ fontSize: getFontSize(item.numero), textAlign: 'center', fontWeight: 900 }}>
                      {item.numero}
                    </Typography>
                  ) : (
                    <Typography sx={{ fontSize: { xs: 13, sm: 15 }, textAlign: 'center' }}>
                      {emoji}
                    </Typography>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {limiteRenderizado < filteredNumeros.length && (
          <Box sx={{ py: 1.5, textAlign: 'center' }}>
            <Typography variant="caption" sx={{ color: '#94a3b8', fontWeight: 800 }}>
              Cargando más números ({limiteRenderizado} de {filteredNumeros.length})...
            </Typography>
          </Box>
        )}

        {filteredNumeros.length === 0 && (
          <Box sx={{ p: 3, textAlign: 'center', color: '#64748b' }}>
            <Icon sx={{ fontSize: 28, mb: 0.5, color: '#94a3b8' }}>search_off</Icon>
            <Typography variant="caption" fontWeight={800} display="block">
              No se encontraron números que coincidan con &ldquo;{searchQuery}&rdquo;
            </Typography>
          </Box>
        )}
      </Box>

      {/* Botones de acción */}
      {numerosSeleccionados.length > 0 && (
        <Box sx={{ mt: 1.5 }}>
          {rifa.reservas_online === '1' ? (
            <Box sx={{ display: 'flex', gap: 1.2, flexDirection: { xs: 'column', sm: 'row' } }}>
              <Button
                color="primary"
                onClick={handleOpenFormulario}
                variant="contained"
                fullWidth
                startIcon={<Icon>bolt</Icon>}
                sx={{
                  py: 1.4,
                  fontWeight: 900,
                  fontSize: '0.98rem',
                  borderRadius: '10px',
                  boxShadow: '0 4px 16px rgba(45, 52, 132, 0.3)',
                }}
              >
                Reservar Online ({numerosSeleccionados.length})
              </Button>
              <Button
                color="success"
                onClick={handleSolicitarReserva}
                variant="contained"
                size="large"
                fullWidth
                startIcon={<Icon>chat</Icon>}
                sx={{
                  py: 1.4,
                  fontWeight: 900,
                  fontSize: '0.98rem',
                  borderRadius: '10px',
                  boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
                }}
              >
                Solicitar por WhatsApp
              </Button>
            </Box>
          ) : (
            <Button
              variant="contained"
              color="success"
              onClick={handleSolicitarReserva}
              size="large"
              fullWidth
              startIcon={<Icon>chat</Icon>}
              sx={{
                py: 1.5,
                fontWeight: 900,
                fontSize: '1.02rem',
                borderRadius: '10px',
                boxShadow: '0 4px 16px rgba(16, 185, 129, 0.3)',
              }}
            >
              Solicitar {numerosSeleccionados.length} Boletos por WhatsApp
            </Button>
          )}
        </Box>
      )}

      {/* Modal del formulario de reserva */}
      <FormularioReserva
        open={formularioOpen}
        onClose={handleCloseFormulario}
        numerosSeleccionados={numerosSeleccionados}
        rifa={rifa}
        onSuccess={handleFormularioSuccess}
      />

      {/* Modal de aviso para Rifa Inactiva o Finalizada */}
      <Dialog
        open={modalInactivoOpen}
        onClose={() => setModalInactivoOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '18px',
            p: { xs: 2, sm: 3 },
            maxWidth: 440,
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 1 }}>
          <Box
            sx={{
              width: 68,
              height: 68,
              borderRadius: '18px',
              bgcolor: 'rgba(239, 68, 68, 0.1)',
              color: '#dc2626',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 2,
            }}
          >
            <Icon sx={{ fontSize: 36 }}>lock</Icon>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: '#1e293b', fontSize: { xs: '1.25rem', sm: '1.45rem' } }}>
            Rifa Inactiva o Finalizada
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 1, sm: 2 } }}>
          <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.7, fontSize: '0.95rem' }}>
            Esta rifa ya no se encuentra activa. En este momento no se permite la reserva de boletos.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 1, pt: 1.5 }}>
          <Button
            variant="contained"
            onClick={() => setModalInactivoOpen(false)}
            sx={{
              borderRadius: '10px',
              fontWeight: 800,
              px: 5,
              py: 1.2,
              bgcolor: theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Boleto Ganador */}
      <Dialog
        open={modalGanadorOpen}
        onClose={() => setModalGanadorOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: { xs: 2.5, sm: 3 },
            maxWidth: 360,
            width: '100%',
            textAlign: 'center',
            bgcolor: theme.palette.mode === 'dark' ? '#131b2e' : '#ffffff',
            color: theme.palette.mode === 'dark' ? '#f8fafc' : '#0f172a',
            boxShadow: theme.palette.mode === 'dark' ? '0 25px 50px rgba(0, 0, 0, 0.6)' : '0 20px 50px rgba(0,0,0,0.15)',
            border: theme.palette.mode === 'dark' ? '1px solid rgba(255, 255, 255, 0.1)' : 'none',
          },
        }}
      >
        <DialogTitle sx={{ pb: 1, pt: 1 }}>
          <Box
            sx={{
              width: 68,
              height: 68,
              borderRadius: '18px',
              bgcolor: 'rgba(245, 158, 11, 0.15)',
              color: '#f59e0b',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 1.5,
              border: '2px solid rgba(245, 158, 11, 0.3)',
            }}
          >
            <span style={{ fontSize: 34 }}>🏆</span>
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 900, color: theme.palette.mode === 'dark' ? '#f8fafc' : '#1e293b', fontSize: { xs: '1.25rem', sm: '1.4rem' } }}>
            Boleto Ganador
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ px: { xs: 1, sm: 2 }, pb: 2 }}>
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              px: 3.5,
              py: 1,
              borderRadius: '12px',
              bgcolor: '#f59e0b',
              color: '#ffffff',
              fontWeight: 900,
              fontSize: '1.6rem',
              letterSpacing: 1,
              boxShadow: '0 4px 14px rgba(245, 158, 11, 0.35)',
              my: 0.5,
            }}
          >
            #{boletoGanadorSeleccionado}
          </Box>
        </DialogContent>

        <DialogActions sx={{ justifyContent: 'center', pb: 1 }}>
          <Button
            variant="contained"
            onClick={() => setModalGanadorOpen(false)}
            sx={{
              borderRadius: '10px',
              fontWeight: 800,
              px: 4,
              py: 1,
              bgcolor: theme.palette.mode === 'dark' ? '#434bbd' : theme.palette.primary.main,
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? '#373ea8' : theme.palette.primary.dark,
              },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Tablero;
